import { capitalizeFirstLetter } from "@repo/mfe-core";
import "./InventorySidePanel.css";

import {
  ApplicationIcon,
  Box,
  ButtonIcon,
  Icon,
  SidePanel,
  TabContent,
  TabGroup,
  TabItem,
} from "@saviynt/design-system";
import classNames from "classnames";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import MetaDataTab from "./MetaDataTab/MetaDataTab";
import SummaryTab from "./SummaryTab";
import { Resource } from "@repo/api/schemas/policies.schema";
import FindingsTab from "./FindingsTab";
import { getApplicationSourceIcon } from "../../PoliciesSidepanel/PoliciesSidepanel";

export type VariantType = "findings" | "inventory" | "policies";
export type metadata = {
  id: string;
  assetName: string;
  connectionName: string;
  source: string;
  displayName: string;
  activeTabId: string;
};
interface SidecarProps {
  isSidecarOpen: boolean;
  closeSidecar: Dispatch<SetStateAction<boolean>>;
  sidecarMetaData: Resource | metadata;
  bridgePosition?: { top: number; left: number; height?: number };
  variant: VariantType;
  isCloseButtonHidden?: boolean;
  className?: string;
}

export default function InventorySidePanel({
  sidecarMetaData,
  isSidecarOpen,
  closeSidecar,
  bridgePosition,
  variant,
  isCloseButtonHidden = false,
  className = "",
}: SidecarProps) {
  const intl = useIntl();
  const { id } = sidecarMetaData;
  let name = "";
  let connectionName = "";
  let source = "";

  if (variant === "inventory") {
    name = (sidecarMetaData as Resource).name;
  } else {
    const { displayName, assetName } = sidecarMetaData as metadata;
    name = displayName || assetName;
    connectionName = (sidecarMetaData as metadata).connectionName;
    source = (sidecarMetaData as metadata).source;
  }

  const [accountMetaData, setAccountMetaData] = useState(null);

  const [activeTab, setActiveTab] = useState("1");
  const [prevId, setPrevId] = useState(id);

  // Reset to Summary tab and clear metadata when viewing a different asset
  // Tracks previous id to prevent unnecessary resets on re-renders
  useEffect(() => {
    if (id !== prevId) {
      setActiveTab("1");
      setAccountMetaData(null);
      setPrevId(id);
    }
  }, [id, prevId]);

  const dataTestId = `NHI-${capitalizeFirstLetter(variant)}-SidePanel`;

  const handleChange = (value: SetStateAction<string>) => {
    setActiveTab(value);
  };

  const renderHeaderIcon = (source: string) => {
    const icon = (
      <ApplicationIcon
        logo={getApplicationSourceIcon(source)}
        dataTestId={dataTestId}
      />
    );

    if (!isCloseButtonHidden) return icon;

    return (
      <div className="flex justify-center items-center">
        <ButtonIcon
          size="large"
          kind="ghost"
          icon={<Icon kind="ChevronLeft" size="small" />}
          onClick={closeSidecar}
          ariaLabel={intl.formatMessage({
            defaultMessage: "Back",
            id: "4bsr8c",
            description: "Aria label for back button",
          })}
          dataTestId="NHI-Inventory-Table-SidePanel-Back-Button"
        />
        {icon}
      </div>
    );
  };

  const sidecarContent = () => (
    <div className="Policies-SidePanel-layoutWidth">
      <TabGroup value={activeTab} onChange={handleChange}>
        <TabItem
          label={intl.formatMessage({
            id: "U8GW9W",
            defaultMessage: "Summary",
            description:
              "Tab label for the summary section in NHI asset details sidecar panel",
          })}
          value="1"
          icon={<Icon kind="Document" size="medium" />}
          dataTestId={`NHI-${capitalizeFirstLetter(variant)}-Summary-Tab`}
        />
        <TabItem
          label={intl.formatMessage({
            id: "UbxI5l",
            defaultMessage: "Findings",
            description:
              "Tab label for the risks section in NHI asset details sidecar panel",
          })}
          value="2"
          icon={<Icon kind="CriticalHighRiskHexagon" size="medium" />}
          dataTestId={`NHI-${capitalizeFirstLetter(variant)}-Findings-Tab`}
        />
        {accountMetaData && Object.keys(accountMetaData).length > 0 && (
          <TabItem
            label={intl.formatMessage({
              id: "aFSHdR",
              defaultMessage: "Metadata",
              description:
                "Tab label for the metadata section in NHI asset details sidecar panel",
            })}
            value="3"
            icon={<Icon kind="DataTable" size="medium" />}
            dataTestId={`NHI-${capitalizeFirstLetter(variant)}-MetaData-Tab`}
          />
        )}
      </TabGroup>
      <TabContent
        value={activeTab}
        tabValue="1"
        className="Policies-Inventory-SidePanel-tabContent"
      >
        <SummaryTab
          assetId={String(id)}
          variant={variant}
          // @ts-expect-error: AccountMetaData type is currently incompatible with AssetMetadata type expected by SummaryTab, will be resolved in upcoming PRs
          setAccountMetaData={setAccountMetaData}
        />
      </TabContent>
      <TabContent
        value={activeTab}
        tabValue="2"
        className="Policies-Inventory-SidePanel-tabContent"
      >
        <FindingsTab assetId={String(id)} variant={variant} />
      </TabContent>

      {accountMetaData && Object.keys(accountMetaData).length > 0 && (
        <TabContent
          value={activeTab}
          tabValue="3"
          className="Policies-Inventory-SidePanel-tabContent"
        >
          <MetaDataTab data={accountMetaData} />
        </TabContent>
      )}
    </div>
  );

  return (
    <Box
      className="Nhi-Inventory-Table-SidePanel"
      style={
        {
          "--bridge-top": `${bridgePosition?.top || 0}px`,
          "--bridge-height": `${bridgePosition?.height || 0}px`,
        } as React.CSSProperties
      }
    >
      <SidePanel
        header={{
          title: name,
          description: connectionName,
          headerIcon: renderHeaderIcon(source),
        }}
        layout={sidecarContent()}
        isOpen={isSidecarOpen}
        onClose={closeSidecar}
        isSidePanelNavigatingList={false}
        className={classNames(
          "Policies-Inventory-SidePanel",
          className,
          "with-bridge",
          {
            "Policies-Inventory-SidePanel--noCloseButton": isCloseButtonHidden,
          },
        )}
        classNames={{
          headerClassName: "Policies-Inventory-SidePanel-header",
        }}
        dataTestId={dataTestId}
      />
    </Box>
  );
}
